import { useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  DatePicker,
  Select,
  Modal,
  Tooltip,
  Segmented,
} from "antd";
import {
  Plus,
  Edit,
  Archive,
  MessageSquare,
  Bell,
  RotateCcw,
  Repeat,
  User,
} from "lucide-react";
import dayjs from "dayjs";

const { Search } = Input;

const STATUS_COLORS = {
  PENDING: "orange",
  IN_PROGRESS: "blue",
  COMPLETED: "green",
  CANCELLED: "default",
};

const STATUS_LABELS = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En Progreso",
  COMPLETED: "Completada",
  CANCELLED: "Cancelada",
};

const PRIORITY_COLORS = {
  LOW: "green",
  MEDIUM: "orange",
  HIGH: "red",
};

const PRIORITY_LABELS = {
  LOW: "Baja",
  MEDIUM: "Media",
  HIGH: "Alta",
};

function TasksList({
  tasks,
  loading,
  onCreateTask,
  onEditTask,
  onArchiveTask,
  onRestoreTask,
  onViewTask,
  onRefresh,
  params,
  onParamsChange,
}) {
  const [viewMode, setViewMode] = useState("active"); // "active" | "archived" | "completed"
  const columns = [
    {
      title: "Fecha",
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YYYY") : <span className="text-gray-400">Sin fecha</span>,
      sorter: true,
    },
    {
      title: "Titulo",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title, record) => (
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: record.color }}
          />
          <span
            className="cursor-pointer hover:text-blue-500"
            onClick={() => onViewTask(record)}
          >
            {title}
          </span>
          {record.isRecurring && (
            <Tooltip title="Tarea periodica">
              <Repeat size={14} className="text-blue-500" />
            </Tooltip>
          )}
          {record.hasReminder && (
            <Tooltip title="Tiene recordatorio">
              <Bell size={14} className="text-orange-500" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: "Prioridad",
      dataIndex: "priority",
      key: "priority",
      width: 90,
      render: (priority) => (
        <Tag color={PRIORITY_COLORS[priority]}>{PRIORITY_LABELS[priority]}</Tag>
      ),
    },
    {
      title: "Creado por",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 120,
      render: (user) => user?.firstName || "Usuario",
    },
    {
      title: "Asignado a",
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 120,
      render: (user) =>
        user ? (
          <span className="flex items-center gap-1">
            <User size={14} className="text-gray-500" />
            {user.firstName || user.email}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "",
      dataIndex: "commentsCount",
      key: "comments",
      width: 50,
      render: (count) =>
        count > 0 ? (
          <Tooltip title={`${count} comentario(s)`}>
            <span className="flex items-center gap-1 text-gray-500">
              <MessageSquare size={14} />
              {count}
            </span>
          </Tooltip>
        ) : null,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              size="small"
              icon={<Edit size={16} />}
              onClick={() => onEditTask(record)}
            />
          </Tooltip>
          {record.isArchived ? (
            <Tooltip title="Restaurar">
              <Button
                type="text"
                size="small"
                icon={<RotateCcw size={16} />}
                onClick={() => {
                  Modal.confirm({
                    title: "Restaurar tarea",
                    content: "La tarea volvera a estar activa.",
                    onOk: () => onRestoreTask(record.id),
                  });
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Archivar">
              <Button
                type="text"
                size="small"
                danger
                icon={<Archive size={16} />}
                onClick={() => {
                  Modal.confirm({
                    title: "Archivar tarea",
                    content:
                      "La tarea sera archivada y podras restaurarla mas adelante.",
                    onOk: () => onArchiveTask(record.id),
                  });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = (value) => {
    onParamsChange({ ...params, search: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    const newParams = { ...params, page: 1 };
    if (value) {
      newParams.status = value;
    } else {
      delete newParams.status;
    }
    onParamsChange(newParams);
  };

  const handleDateRangeChange = (dates) => {
    const newParams = { ...params, page: 1 };
    if (dates) {
      newParams.date_after = dates[0].format("YYYY-MM-DD");
      newParams.date_before = dates[1].format("YYYY-MM-DD");
    } else {
      delete newParams.date_after;
      delete newParams.date_before;
    }
    onParamsChange(newParams);
  };

  const handleViewModeChange = (value) => {
    setViewMode(value);
    const newParams = { page: 1 };

    if (value === "archived") {
      newParams.is_archived = true;
    } else if (value === "completed") {
      newParams.status = "COMPLETED";
    }
    // "active" mode uses default (non-archived, non-completed)

    onParamsChange(newParams);
  };

  const getTitle = () => {
    switch (viewMode) {
      case "archived":
        return "Tareas Archivadas";
      case "completed":
        return "Tareas Finalizadas";
      default:
        return "Tareas Activas";
    }
  };

  return (
    <div className="mt-6 bg-white rounded-lg p-4 border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{getTitle()}</h3>
        <div className="flex items-center gap-3">
          <Segmented
            value={viewMode}
            onChange={handleViewModeChange}
            options={[
              { label: "Activas", value: "active" },
              { label: "Finalizadas", value: "completed" },
              { label: "Archivadas", value: "archived" },
            ]}
          />
          {viewMode === "active" && (
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={onCreateTask}
            >
              Nueva Tarea
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-4 mb-4 flex-wrap">
        <Search
          placeholder="Buscar tareas..."
          onSearch={handleSearch}
          style={{ width: 200 }}
          allowClear
        />
        <DatePicker.RangePicker
          onChange={handleDateRangeChange}
          format="DD/MM/YYYY"
          placeholder={["Desde", "Hasta"]}
          style={{ width: 240 }}
        />
        {viewMode === "active" && (
          <Select
            placeholder="Estado"
            onChange={handleStatusChange}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: "PENDING", label: "Pendiente" },
              { value: "IN_PROGRESS", label: "En Progreso" },
            ]}
          />
        )}
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={tasks.results || []}
        rowKey="id"
        size="small"
        pagination={{
          current: params.page || 1,
          pageSize: 10,
          total: tasks.count,
          onChange: (page) => onParamsChange({ ...params, page }),
          showSizeChanger: false,
        }}
        onRow={(record) => ({
          onClick: () => onViewTask(record),
          style: { cursor: "pointer" },
        })}
        scroll={{ x: 800 }}
      />
    </div>
  );
}

export default TasksList;
